import axios, { AxiosResponse } from "axios";
import React, {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { uuidv7 } from "uuidv7";
import { DragEndEvent } from "@dnd-kit/core";

import AnimatedDiv from "src/app/components/AnimatedDiv/AnimatedDiv";
import { Button } from "src/app/components/Button/Button";
import CustomInput from "src/app/components/CustomInput/CustomInput";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import Loader from "src/app/components/Loader/Loader";
import NoResultsMessage from "src/app/components/NoResultsMessage/NoResultsMessage";
import { translateMessage } from "src/app/methods/translateMessage";
import { RemoveModal } from "src/app/modals/RemoveModal/RemoveModal";
import { ReactComponent as PlusIcon } from "src/images/plus-transparent.svg";
import { RootState } from "src/redux/reducers";
import { API_URLS } from "src/utils/API_URLS";
import { parseErrorMessages, showErrorToast } from "src/utils/methods";
import { generateRankString } from "src/utils/rankStrings";
import responseCodes from "src/utils/responseCodes";
import { DEFAULT_RANK } from "src/constants/ranks";
import ExtensionView from "../Extensions/ExtensionView";
import { Template } from "./types";
import {
  handleUpdateProjectTemplate,
  handleUpdateProjectTemplateRank,
} from "./utils";
import DraggableTemplateItem from "./DraggableTemplateItem";
import { VerticalDragAndDropList } from "src/app/components/VerticalDragAndDropList/VerticalDragAndDropList";

interface TemplatesListProps {
  setEditedTemplate: Dispatch<SetStateAction<Template | null>>;
}

function TemplatesList({ setEditedTemplate }: TemplatesListProps) {
  const [value, setValue] = useState("");
  const [templateToRemove, setTemplateToRemove] = useState<Template | null>(
    null,
  );
  const [templatesList, setTemplatesList] = useState<Template[]>([]);
  const [isTemplatesListLoading, setIsTemplatesListLoading] = useState(false);

  const intl = useIntl();
  const dispatch = useDispatch();

  const { activeWorkspaceUuid } = useSelector(
    (state: RootState) => state.mainReducer,
  );

  const handleAddNewTemplate = async () => {
    const templateUuid = uuidv7();

    const lastElementRank =
      templatesList[templatesList.length - 1]?.rank || DEFAULT_RANK;

    const newRank = generateRankString(lastElementRank, "");

    const newTemplateData = {
      uuid: templateUuid,
      name: "New Template",
      description: "",
      globalProjectMetaFieldsUuids: {},
      globalTaskMetaFieldsUuids: {},
      automations: [],
      extensions: [],
      rank: newRank,
      enabled: false,
    };

    setEditedTemplate(newTemplateData);

    try {
      const response = await axios.post(API_URLS.createProjectTemplate, {
        ...newTemplateData,
        rank: null,
        wsWorkspaceUuid: activeWorkspaceUuid,
      });
      if (response.status !== responseCodes["200_OK"]) {
        throw new Error("Could not create template");
      }
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data?.errors) {
        const errorMessage = parseErrorMessages(error.response.data.errors)[0]
          .message;
        showErrorToast({
          id: errorMessage,
          defaultMessage: errorMessage,
        });
      } else {
        showErrorToast({
          id: "ws_could_not_create_project_template",
          defaultMessage: "An error occurred while creating the template",
        });
      }
      setEditedTemplate(null);
      console.error(error);
    }
  };

  const getProjectTemplatesList = useCallback(async (workspaceUuid: string) => {
    setIsTemplatesListLoading(true);
    try {
      const res: AxiosResponse<{
        content: { wsProjectTemplates: Template[] };
      }> = await axios.get(
        API_URLS.getSettingsProjectTemplates.replace(
          ":wsWorkspaceUuid:",
          workspaceUuid,
        ),
      );
      if (res.status === responseCodes["200_OK"]) {
        const templates = res.data.content.wsProjectTemplates;
        setTemplatesList(templates);
      } else {
        throw new Error("Could not load templates");
      }
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data?.errors) {
        const errorMessage = parseErrorMessages(error.response.data.errors)[0]
          .message;
        showErrorToast({
          id: errorMessage,
          defaultMessage: errorMessage,
        });
      } else {
        showErrorToast({
          id: "ws_could_not_load_templates",
          defaultMessage: "An error occurred while loading templates",
        });
      }
      console.error(error);
    } finally {
      setIsTemplatesListLoading(false);
    }
  }, []);

  const handleDuplicateTemplate = async (duplicatedTemplate: Template) => {
    const templateUuid = uuidv7();
    setTemplatesList((prev) => [
      ...prev,
      {
        ...duplicatedTemplate,
        name: `Copy of ${duplicatedTemplate.name}`,
        uuid: templateUuid,
      },
    ]);
    try {
      const response = await axios.post(API_URLS.duplicateProjectTemplate, {
        name: `Copy of ${duplicatedTemplate.name}`,
        uuid: templateUuid,
        sourceWsProjectTemplateUuid: duplicatedTemplate.uuid,
      });
      if (response.status !== responseCodes["200_OK"]) {
        throw new Error("Could not duplicate template");
      }
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data?.errors) {
        const errorMessage = parseErrorMessages(error.response.data.errors)[0]
          .message;
        showErrorToast({
          id: errorMessage,
          defaultMessage: errorMessage,
        });
      } else {
        showErrorToast({
          id: "ws_could_not_duplicate_template",
          defaultMessage: "An error occurred while duplicating the template",
        });
      }
      console.error(error);
      getProjectTemplatesList(activeWorkspaceUuid);
    }
  };

  const handleDeleteTemplate = async (removedTemplateUuid: string) => {
    try {
      const response = await axios.delete(API_URLS.deleteProjectTemplate, {
        data: {
          uuid: removedTemplateUuid,
        },
      });
      if (response.status === responseCodes["200_OK"]) {
        setTemplatesList((prev) =>
          prev.filter((t) => t.uuid !== removedTemplateUuid),
        );
      } else {
        throw new Error("Could not delete template");
      }
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data?.errors) {
        const errorMessage = parseErrorMessages(error.response.data.errors)[0]
          .message;
        showErrorToast({
          id: errorMessage,
          defaultMessage: errorMessage,
        });
      } else {
        showErrorToast({
          id: "ws_could_not_remove_template",
          defaultMessage: "An error ocurred while removing the template",
        });
      }
      console.error(error);
      getProjectTemplatesList(activeWorkspaceUuid);
    }
  };

  const sortedTemplatesList = [...templatesList].sort((a, b) =>
    a.rank.localeCompare(b.rank),
  );

  const displayedTemplatesList = sortedTemplatesList.filter((template) =>
    template.name.toLowerCase().includes(value.toLocaleLowerCase()),
  );

  const handleEnableTemplate = (
    e: ChangeEvent<HTMLInputElement>,
    template: Template,
  ) => {
    const { checked } = e.target;

    setTemplatesList((prev) => {
      return prev.map((t) => {
        if (t.uuid === template.uuid) {
          return { ...t, enabled: checked };
        }
        return t;
      });
    });
    handleUpdateProjectTemplate({
      data: {
        ...template,
        wsWorkspaceUuid: activeWorkspaceUuid,
        enabled: checked,
      },
      catchCallback: () =>
        setTemplatesList((prev) => {
          return prev.map((t) => {
            if (t.uuid === template.uuid) {
              return { ...t, enabled: !checked };
            }
            return t;
          });
        }),
      dispatch,
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = displayedTemplatesList.findIndex(
      (t) => t.uuid === active.id,
    );
    const newIndex = displayedTemplatesList.findIndex(
      (t) => t.uuid === over.id,
    );

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    const draggedTemplate = displayedTemplatesList[oldIndex];

    // Create a new list with the item moved
    const newList = [...displayedTemplatesList];
    newList.splice(oldIndex, 1);
    newList.splice(newIndex, 0, draggedTemplate);

    // Calculate new rank
    let newRank: string;
    if (newIndex === 0) {
      // Moving to the top
      newRank = generateRankString("", newList[1]?.rank || "");
    } else if (newIndex >= newList.length - 1) {
      // Moving to the bottom
      newRank = generateRankString(newList[newList.length - 2]?.rank || "", "");
    } else {
      // Moving in between
      newRank = generateRankString(
        newList[newIndex - 1]?.rank || "",
        newList[newIndex + 1]?.rank || "",
      );
    }

    // Update local state
    setTemplatesList((prev) => {
      return prev.map((t) => {
        if (t.uuid === draggedTemplate.uuid) {
          return { ...t, rank: newRank };
        }
        return t;
      });
    });

    // Update rank on the server
    handleUpdateProjectTemplateRank(
      {
        uuid: draggedTemplate.uuid,
        rank: newRank,
      },
      dispatch,
    );
  };

  useEffect(() => {
    getProjectTemplatesList(activeWorkspaceUuid);
  }, [activeWorkspaceUuid, getProjectTemplatesList]);

  return (
    <>
      <ExtensionView
        title={
          <IDHFormattedMessage
            id="ws_template_manager"
            defaultMessage="Template Manager"
          />
        }
      >
        <AnimatedDiv>
          <div className="global-fields__input-wrapper">
            <CustomInput
              type="text"
              placeholder={translateMessage({
                intl,
                id: "ws_search_for_template",
                defaultMessage: "Search for a template",
              })}
              value={value}
              onChange={(e) => setValue(e.target.value)}
            />
            <Button size="large" variant="blue" onClick={handleAddNewTemplate}>
              <PlusIcon />
              <IDHFormattedMessage
                id="ws_create_template"
                defaultMessage="Create Template"
              />
            </Button>
          </div>
          <div className="template-manager__headers grid--templates-list">
            <div>
              <IDHFormattedMessage id="ws_template" defaultMessage="Template" />
            </div>
            <div>
              <IDHFormattedMessage id="ws_active" defaultMessage="Active" />{" "}
            </div>
            <div>
              <IDHFormattedMessage id="ws_actions" defaultMessage="Actions" />
            </div>
          </div>
          {isTemplatesListLoading ? (
            <Loader />
          ) : (
            <VerticalDragAndDropList.DndContext onDragEnd={handleDragEnd}>
              <VerticalDragAndDropList.SortableContext
                items={displayedTemplatesList.map((t) => t.uuid)}
              >
                <ul className="template-manager__list">
                  {displayedTemplatesList.length > 0 ? (
                    displayedTemplatesList.map((template) => (
                      <DraggableTemplateItem
                        key={template.uuid}
                        template={template}
                        handleEnableTemplate={handleEnableTemplate}
                        setEditedTemplate={setEditedTemplate}
                        handleDuplicateTemplate={handleDuplicateTemplate}
                        setTemplateToRemove={setTemplateToRemove}
                      />
                    ))
                  ) : (
                    <NoResultsMessage />
                  )}
                </ul>
              </VerticalDragAndDropList.SortableContext>
            </VerticalDragAndDropList.DndContext>
          )}
        </AnimatedDiv>
      </ExtensionView>
      {templateToRemove && (
        <RemoveModal
          objectNames={[templateToRemove.name]}
          onClose={() => setTemplateToRemove(null)}
          removeFunction={() => handleDeleteTemplate(templateToRemove.uuid)}
        />
      )}
    </>
  );
}

export default TemplatesList;
